// Libs
import classNames from 'classnames/bind';
import { pdf } from '@react-pdf/renderer';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  Loading,
  BaseTable,
  BaseAvatar,
  BaseStatus,
  BaseToolbar,
  BaseFilters,
  ConfirmModal,
  SortComponent,
  BaseMoreAction,
  UnderDevelopment,
  BaseDynamicSelect,
} from '~/components/common';
import { ChangeStatusInvoiceModal, InvoicePdf } from '~/components/module/invoice';
// Others
import {
  IInvoice,
  IInvoicePdf,
  IInvoiceDetail,
  IParamQueryInvoice,
  IChangeStatusInvoice,
} from '~/utils/interface/invoice';
import { StorageEnum } from '~/utils/enum/base';
import { invoiceActions } from '~/thunks/invoice/invoiceSlice';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IPaginationResponse } from '~/utils/interface/base';
import { DateFormatEnum, MethodEnum, RoleEnum } from '~/utils/enum/common';
import {
  changeStatusInvoice,
  deleteInvoice,
  getDetailInvoice,
  getListInvoice,
  sendMailInvoice,
} from '~/thunks/invoice/invoiceThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { formatCurrencyUSD, formatDate, formatTitleCase, getFullName } from '~/utils/helper/common';
import {
  IBaseMoreAction,
  IBaseOption,
  IBodyUpdate,
  IFilterValueChange,
  IHeaderCellType,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import {
  EMPTY_VALUE,
  EMPTY_STRING,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_SORT_INVOICE,
  FILTER_DEFAULT_VALUES,
  DEFAULT_INVOICE_FILE_NAME_PDF,
  optionStatusInvoice,
} from '~/utils/constants/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { INVOICE_PERMISSIONS } from '~/utils/constants/permissions';
import { usePermissions } from '~/utils/hooks/userPermissions';
// Styles, images, icons
import styles from './Invoice.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsInvoice = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string, item: IInvoice) => void,
  onEdit: (id: string) => void,
  onSendInvoice: (invoiceId: string) => void,
  onChangeStatus: (item: IInvoice) => void,
  onDelete: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IInvoice>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'invoiceNumber',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_invoice')}</Box>
          <SortComponent name='invoice number' sortType={getSortType('invoice number')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => record?.invoiceNumber || EMPTY_VALUE,
    },
    {
      id: 'client',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_client')}</Box>
          <SortComponent name='client' sortType={getSortType('client')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) =>
        record?.client ? (
          <Stack direction={'row'} gap={4} alignItems={'center'}>
            <BaseAvatar dataAccount={record.client} size={32} fontSize={14} />
            <Stack direction={'column'}>
              <Typography className={cx('textName')}>
                {getFullName({
                  firstName: record?.client?.firstName,
                  lastName: record?.client?.lastName,
                })}
              </Typography>
              <Typography className={cx('textEmail')}>{record?.client?.clientContacts?.[0]?.email}</Typography>
            </Stack>
          </Stack>
        ) : (
          EMPTY_VALUE
        ),
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_total')}</Box>
          <SortComponent name='total' sortType={getSortType('total')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => {
        return (
          <Typography className={cx('textPrice')}>
            {formatCurrencyUSD({ value: record?.total }) || EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'amountPaid',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_amount_paid')}</Box>
          <SortComponent name='amount paid' sortType={getSortType('amount paid')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => {
        return (
          <Typography className={cx('textPrice')}>
            {formatCurrencyUSD({ value: record?.amountPaid }) || EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'balanceDue',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_balance_due')}</Box>
          <SortComponent name='balance due' sortType={getSortType('balance due')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => {
        return (
          <Typography className={cx('textPrice')}>
            {formatCurrencyUSD({ value: record?.balanceDue }) || EMPTY_VALUE}
          </Typography>
        );
      },
    },
    {
      id: 'paymentTerms',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_term')}</Box>
          <SortComponent name='term' sortType={getSortType('term')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => formatTitleCase(record?.paymentTerms),
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) =>
        record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE,
    },
    {
      id: 'invoiceDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_invoice_date')}</Box>
          <SortComponent name='invoice date' sortType={getSortType('invoice date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatDate(record?.invoiceDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'dueDate',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_due_date')}</Box>
          <SortComponent name='due date' sortType={getSortType('due date')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInvoice) => {
        return <Typography>{formatDate(record?.dueDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IInvoice) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id, record),
                },
              ]
            : []),
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          {
            label: t('invoice_title_send_invoice'),
            icon: (
              <img
                src={icons.tableIconActionSend}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onSendInvoice(record.id),
          },
          {
            label: t('common_action_change_status', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.commonIconRepeatBusiness}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onChangeStatus(record),
          },
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record.id),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Invoice = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateInvoices, canViewInvoices, canDetailInvoices, canEditInvoices, canDeleteInvoices } =
    usePermissions(INVOICE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshInvoiceList } = useAppSelector((state) => state.invoice);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_INVOICE);
  const [filterParams, setFilterParams] = useState<IParamQueryInvoice>(FILTER_DEFAULT_VALUES as IParamQueryInvoice);

  const [paramObject, setParamObject] = useState<IParamQueryInvoice>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [invoiceList, setInvoiceList] = useState<IInvoice[]>([]);
  const [idInvoice, setIdInvoice] = useState<string>(EMPTY_STRING);
  const [currentStatus, setCurrentStatus] = useState<string>(EMPTY_STRING);
  const [isShowChangeStatusModal, setIsShowChangeStatusModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListInvoice(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshInvoiceList) return;

    handleGetListInvoice(paramObject);

    dispatch(invoiceActions.setRefreshInvoiceList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshInvoiceList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInvoice = (params: IParamQueryInvoice) => {
    setIsLoading(true);

    dispatch(getListInvoice(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res?.data;

        if (!responses) return;

        setInvoiceList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createInvoice);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createInvoice);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createInvoice);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createInvoice);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createInvoice);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createInvoice);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createInvoice);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createInvoice);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createInvoice);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateInvoice(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateInvoice(id));
        break;
    }
  };

  const handleView = (_id: string, item: IInvoice) => {
    if (!item?.order) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(`${adminRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(
          `${salesAdminRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.SALES_PERSON:
        navigate(
          `${salesPersonRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          `${operationsManagerRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${
            item?.order?.orderNumber
          }#invoice`
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          `${warehouseLeadRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          `${warehouseStaffRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(
          `${foremanRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`
        );
        break;
      case RoleEnum.CREW:
        navigate(`${crewRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`);
        break;
      case RoleEnum.LOADER:
        navigate(`${loaderRouteAbsolute.detailOrder(item?.order?.id)}?orderNumber=${item?.order?.orderNumber}#invoice`);
        break;
    }
  };

  const handleSendInvoice = async (invoiceId: string) => {
    setIsLoading(true);

    const queryDetailForm = {
      id: invoiceId,
      params: { method: MethodEnum.UPDATE },
    };

    const dataPdf = await dispatch(getDetailInvoice(queryDetailForm))
      .unwrap()
      .then((res) => {
        const data = res?.data as IInvoiceDetail;

        const dataDetail: IInvoicePdf = {
          clientName: getFullName({ firstName: data?.client?.firstName, lastName: data?.client?.lastName }),
          invoiceNumber: data?.invoiceNumber,
          dueDate: data?.dueDate,
          invoices: data?.invoiceItems,
          subtotal: data?.subtotal,
          taxAmount: data?.taxAmount,
          discountRate: data?.discountRate,
          total: data?.total,
        };

        return dataDetail;
      })
      .catch((_error) => {})
      .finally(() => {});

    if (!dataPdf) {
      setIsLoading(false);
      return;
    }

    const pdfBlob = await generatePdfBlob(dataPdf);

    const formData = new FormData();

    if (!pdfBlob) return;

    formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);

    const payload: IBodyUpdate<FormData> = {
      id: invoiceId,
      body: formData,
    };

    dispatch(sendMailInvoice(payload))
      .unwrap()
      .then((_res) => {})
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const generatePdfBlob = async (data: IInvoicePdf): Promise<Blob | null> => {
    try {
      return await pdf(<InvoicePdf invoicePdf={data} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdInvoice(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdInvoice(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteInvoice(idInvoice))
      .unwrap()
      .then(() => {
        setIdInvoice(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(invoiceActions.setRefreshInvoiceList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusInvoice,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleApplyFilter = (values: IParamQueryInvoice | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES as IParamQueryInvoice);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: string[],
    onChange?: (value: IFilterValueChange<IParamQueryInvoice>) => void
  ) => {
    if (!status) return;

    onChange?.({ name: 'status', value: status });
    setFilterParams({ ...filterParams, status: status });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleChangeStatus = (item: IInvoice) => {
    setCurrentStatus(item?.status);
    setIdInvoice(item?.id);
    setIsShowChangeStatusModal(true);
  };

  const handleCloseChangeStatus = () => {
    setCurrentStatus(EMPTY_STRING);
    setIdInvoice(EMPTY_STRING);
    setIsShowChangeStatusModal(false);
  };

  const handleApplyChangeStatus = (status: string) => {
    const body: IBodyUpdate<IChangeStatusInvoice> = {
      id: idInvoice,
      body: {
        status: status,
      },
    };

    dispatch(changeStatusInvoice(body))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {});
  };
  //#endregion Handle Function

  return (
    <Box id='invoicePage' className={cx('container')}>
      <BaseToolbar title={t('invoice_title_invoices_list')}>
        <BaseFilters<IParamQueryInvoice>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES as IParamQueryInvoice}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseDynamicSelect
                multiple={true}
                gap={10}
                label={t('invoice_filter_title_status')}
                placeholder={t('invoice_filter_title_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(selected) =>
                  handleFilterStatusChange(
                    selected.optionsSelected.map((item) => item?.value),
                    onChange
                  )
                }
              />
            </Stack>
          )}
        </BaseFilters>

        {canCreateInvoices && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('invoice_title_create_invoice')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewInvoices ? invoiceList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsInvoice(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleSendInvoice,
            handleChangeStatus,
            handleDelete,
            canDetailInvoices,
            canEditInvoices,
            canDeleteInvoices
          )}
          onPageChange={handlePageChange}
          {...(canDetailInvoices ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isShowChangeStatusModal && (
        <ChangeStatusInvoiceModal
          status={currentStatus}
          onApply={handleApplyChangeStatus}
          onClose={handleCloseChangeStatus}
        />
      )}
    </Box>
  );
};

export default Invoice;
